// Lien d'invitation du coach (page Athletes) : il le copie et le transmet
// lui-meme (SMS, messagerie...). La personne qui l'ouvre s'inscrit avec son
// compte Google et devient son athlete, dans son club (cf Auth/RejoindreCoach).
import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CopierIcon from '@mui/icons-material/ContentCopy';
import PartagerIcon from '@mui/icons-material/Share';

import {
  adresseInvitation,
  chargerLienInvitation,
  desactiverLienInvitation,
  genererLienInvitation,
} from '../Auth/moi.ts';

export default function LienInvitation({ ouvert, fermer }: { ouvert: boolean; fermer: () => void }) {
  const [jeton, setJeton] = React.useState<string | null>(null);
  const [charge, setCharge] = React.useState(false);
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setInfo(null);
    setCharge(false);
    chargerLienInvitation()
      .then(setJeton)
      .catch((e) => setErreur('Lien indisponible : ' + (e as Error).message))
      .finally(() => setCharge(true));
  }, [ouvert]);

  const agir = async (action: () => Promise<void>) => {
    setEnCours(true);
    setErreur(null);
    setInfo(null);
    try {
      await action();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  const generer = (nouveau: boolean) => agir(async () => {
    if (nouveau && !window.confirm("Générer un nouveau lien ? L'ancien ne fonctionnera plus.")) return;
    setJeton(await genererLienInvitation());
    if (nouveau) setInfo("Nouveau lien généré : l'ancien ne fonctionne plus.");
  });

  const desactiver = () => agir(async () => {
    if (!window.confirm("Désactiver le lien ? Les personnes qui ne l'ont pas encore ouvert ne pourront plus s'inscrire avec.")) return;
    await desactiverLienInvitation();
    setJeton(null);
  });

  const adresse = jeton ? adresseInvitation(jeton) : '';

  const copier = () => agir(async () => {
    await navigator.clipboard.writeText(adresse);
    setInfo('Lien copié : colle-le dans un SMS, un mail ou une messagerie.');
  });

  const partager = () => agir(async () => {
    try {
      await navigator.share({ title: 'Invitation', text: "Inscris-toi pour que je suive ton entraînement :", url: adresse });
    } catch (e) {
      // Partage annule par l'utilisateur : rien a signaler.
      if ((e as Error).name !== 'AbortError') throw e;
    }
  });

  const partageDisponible = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <Dialog open={ouvert} onClose={fermer} fullWidth maxWidth="sm">
      <DialogTitle>Lien d'invitation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Transmets ce lien à une personne que tu veux entraîner. En l'ouvrant, elle s'inscrit avec son compte Google
          et devient ton athlète, dans ton club : tu établis son calendrier dès qu'elle a confirmé. Le même lien sert
          pour plusieurs personnes.
        </Typography>
        {charge && jeton && (
          <TextField
            fullWidth
            size="small"
            label="Lien à transmettre"
            value={adresse}
            onFocus={(e) => e.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copier">
                    <IconButton edge="end" onClick={copier} disabled={enCours} aria-label="Copier le lien">
                      <CopierIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        )}
        {charge && !jeton && !erreur && (
          <Typography>Tu n'as pas de lien actif.</Typography>
        )}
        {info && <Alert severity="success" sx={{ mt: 2 }}>{info}</Alert>}
        {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}
        {charge && jeton && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Button size="small" onClick={() => generer(true)} disabled={enCours}>Générer un nouveau lien</Button>
            <Button size="small" color="error" onClick={desactiver} disabled={enCours}>Désactiver le lien</Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fermer}>Fermer</Button>
        {charge && !jeton && (
          <Button variant="contained" onClick={() => generer(false)} disabled={enCours}>Créer le lien</Button>
        )}
        {charge && jeton && partageDisponible && (
          <Button startIcon={<PartagerIcon />} onClick={partager} disabled={enCours}>Partager</Button>
        )}
        {charge && jeton && (
          <Button variant="contained" startIcon={<CopierIcon />} onClick={copier} disabled={enCours}>Copier</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
